import { Injectable, inject } from '@angular/core';
import { ITodoItem, ITodoListEditItem, ITodoSection } from '@fi-app/fi-onboarding/fi-todo/fi-todo';
import { AccessRightFilterCollectionModel, ILightWeightArea, IAccessRightFilterCollection } from '@fi-shared/models';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';
import { ISectionCardData } from '@fi-app/shared/fi-section-card/fi-section-card';
import { AppTextService } from '@fi-shared/app-text';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiNameDescriptionEditorDialogComponent } from '@fi-app/shared/name-description-input/name-description-editor-dialog/name-description-editor-dialog.component';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { getObjectTypeNameTextId, isDefined } from '@fi-shared/utils';
import { FiTodoListStoreService } from './fi-todo-edit/fi-todo-list-editor-store.service';
import { FiApplicationLanguageType } from '@fi-app/shared/fi-content-language-shared/fi-application-language-type';
import { cloneDeep } from '@fi-app/shared/utils/clone-deep';
import { fiItemsFlatten } from '@fi-app/shared/utils/fi-items-flatten';
import { FiAccessRightHelperService } from '@fi-shared/fi-shared-services/fi-access-right-helper.service';
import { ModuleType } from '@fi-app/shared/types/module-type';
import { FiItemSharingSettingsDialogService } from '@fi-app/shared/access-right-filters/fi-item-sharing-settings-dialog/fi-item-sharing-settings.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { mergeLightWeightObjects } from '@fi-app/shared/utils/merge-light-weight-objects';
import { FiSectionsHelperService } from '@fi-app/shared/fi-sections-dnd-editor/services/fi-sections-helper.service';
import { IContentLanguageLabelModel } from '@fi-app/shared/fi-content-language-shared/fi-content-language-shared';
import { FiDeleteConnectedOboardingElementDialogService } from '../fi-delete-connected-oboarding-element-dialog/fi-delete-connected-oboarding-element-dialog.service';

export enum FiTodoListEditorSections {
    TodoItems = 0,
    SharingSettings = 1,
}

@Injectable()
export class FiTodoHelperService {
    private fiContentLanguageService = inject(FiContentLanguageSharedService);
    private appTextService = inject(AppTextService);
    private fiStateService = inject(FiStateService);
    private fiDialogsService = inject(FiDialogsService);
    private fiTodoListStoreService = inject(FiTodoListStoreService);
    private fiAccessRightHelperService = inject(FiAccessRightHelperService);
    private fiItemSharingSettingsDialogService = inject(FiItemSharingSettingsDialogService);
    private fiUserDataService = inject(FiUserDataService);
    private fiSectionsHelperService = inject(FiSectionsHelperService);
    private fiDeleteConnectedOboardingElementDialogService = inject(FiDeleteConnectedOboardingElementDialogService);

    getTodoListHeaderText(todoListName: string, isCopyMode: boolean): string {
        if (isCopyMode) {
            return (
                this.appTextService.get('onboarding.todo_list_edit') +
                ': (' +
                this.appTextService.get('copy.noun') +
                ') ' +
                todoListName
            );
        }
        if (isDefined(todoListName)) {
            return this.appTextService.get('onboarding.todo_list_edit') + ': ' + todoListName;
        }

        return this.appTextService.get('onboarding.todo_list_new');
    }

    initContentLanguages(todoListEditItem: ITodoListEditItem): ITodoListEditItem {
        this.fiContentLanguageService.initializeContentLanguagesArray(todoListEditItem.Names, todoListEditItem.Names);
        this.fiContentLanguageService.initializeContentLanguagesArray(
            todoListEditItem.Descriptions,
            todoListEditItem.Descriptions,
        );
        todoListEditItem.Sections.forEach((section) => {
            this.fiContentLanguageService.initializeContentLanguagesArray(section.Names, section.Names);
            this.fiContentLanguageService.initializeContentLanguagesArray(section.Descriptions, section.Descriptions);
            section.Items.forEach((item) => {
                this.fiContentLanguageService.initializeContentLanguagesArray(item.Names, item.Names);
                this.fiContentLanguageService.initializeContentLanguagesArray(item.Descriptions, item.Descriptions);
            });
        });

        return todoListEditItem;
    }

    insertCopyLabelIntoContentLanguageLabelModels(
        contentLanguageLabelModels: IContentLanguageLabelModel[],
    ): IContentLanguageLabelModel[] {
        return this.fiContentLanguageService.insertCopyLabelIntoContentLanguageLabelModels(contentLanguageLabelModels);
    }

    getNewTodoListItemInstance(): ITodoListEditItem {
        return {
            Names: this.fiContentLanguageService.getContentLanguageLabelModels(),
            Descriptions: this.fiContentLanguageService.getContentLanguageLabelModels(),
            IsShared: true,
            AccessRightFilterCollection: AccessRightFilterCollectionModel.getNewInstance(),
            MediaBankFileReferences: [],
            Sections: [this.getNewTodoSectionInstance()],
        };
    }

    getNewTodoSectionInstance(): ITodoSection {
        return {
            Names: this.fiContentLanguageService.getContentLanguageLabelModels(),
            Descriptions: this.fiContentLanguageService.getContentLanguageLabelModels(),
            Items: [],
        };
    }

    getNewTodoItemInstance(): ITodoItem {
        return {
            Names: this.fiContentLanguageService.getContentLanguageLabelModels(),
            Descriptions: this.fiContentLanguageService.getContentLanguageLabelModels(),
            AllowComments: false,
            AllowAttachments: false,
            MediaBankFileReferences: [],
        };
    }

    openAddTodoItemDialog(sectionIndex: number): void {
        if (!isDefined(sectionIndex)) {
            sectionIndex = 0;
        }
        const sections = this.fiTodoListStoreService.currentTodoListSections();
        const todoListItem: ITodoItem = this.getNewTodoItemInstance();

        this.fiDialogsService
            .custom({
                contentComponentRef: FiNameDescriptionEditorDialogComponent,
                contentComponentData: {
                    names: todoListItem.Names,
                    descriptions: todoListItem.Descriptions,
                    isNameRequired: true,
                    isDescriptionRequired: true,
                    objectType: ObjectTypes.TodoItem,
                    mediaBankFileReferences: todoListItem.MediaBankFileReferences,
                    showAttachmentsArea: false,
                    nameFormFieldClassName: 'fi-form-field-standard',
                    parentDialogClassName: 'fi-todo-sections-edit',
                    namePlaceholderTextId: 'common.item_name',
                    namePlaceholderHintTextId: 'common.item_name_hint',
                    descriptionPlaceholderTextId: 'common.description',
                    descriptionPlaceholderHintTextId: 'common.item_description_hint',
                    selectedLanguageId: this.fiTodoListStoreService.selectedLanguageId(),
                    isInsertLinkHidden: true,
                },
                hideDialogTopBorder: true,
                headerDividerClass: 'BrandedBorderColor',
                isHeaderDividerShown: true,
                headerText: this.appTextService.get('todo.new_todo_item'),
                preventUppercaseButtonLabel: true,
                yesButtonText: this.appTextService.get('done'),
                config: {
                    panelClass: 'fi-rounded-dialog-panel',
                    maxWidth: '850px',
                    width: '90%',
                    autoFocus: false,
                    restoreFocus: false,
                },
            })
            .onYesButtonClick((result) => {
                todoListItem.Names = result.names;
                todoListItem.Descriptions = result.descriptions;
                todoListItem.MediaBankFileReferences = result.mediaBankFileReferences;
                sections[sectionIndex].Items.push(todoListItem);
                this.onSectionsDataChanged(sections);
                this.fiTodoListStoreService.setCurrentTodoListItem(todoListItem);
                const todoItemIndex = sections[sectionIndex].Items.length - 1;
                this.fiTodoListStoreService.setCurrentTodoListItemIndex(todoItemIndex);
                this.fiTodoListStoreService.setCurrentTodoListSectionIndex(sectionIndex);
            });
    }

    onSectionsDataChanged(sections: ITodoSection[]): void {
        this.fiTodoListStoreService.setTodoListSections(sections);
    }

    saveTodoItem(): void {
        const sections = this.fiTodoListStoreService.currentTodoListSections();
        const todoItem = this.fiTodoListStoreService.currentTodoListItem();
        const todoItemIndex = this.fiTodoListStoreService.currentTodoListItemIndex();
        const todoSectionIndex = this.fiTodoListStoreService.currentTodoListSectionIndex();
        sections[todoSectionIndex].Items[todoItemIndex] = todoItem;
        this.onSectionsDataChanged(sections);
        this.navigateToSectionsEditor();
    }

    getTodoItemsSectionDescription(sections: ITodoSection[]): string {
        const items = sections.flatMap((section) => section.Items);
        if (!items.length) {
            return this.appTextService.get('todo.todo_list_has_no_items');
        }
        const itemsLabel = this.appTextService.get(getObjectTypeNameTextId(ObjectTypes.TodoItem, items.length !== 1));
        return this.appTextService.get('todo.todo_list_has_items', { '{0}': `${items.length} ${itemsLabel}` });
    }

    private getSelectedNoMainAreasLength(availableAccessRightFilterCollection: IAccessRightFilterCollection): number {
        const todoList = this.fiTodoListStoreService.currentTodoListData();

        const outOfScopeAreas: ILightWeightArea[] = this.fiAccessRightHelperService.getOutOfScopeItems(
            availableAccessRightFilterCollection.AccessRightCoreFilter.Areas,
            todoList.AccessRightFilterCollection.AccessRightCoreFilter.Areas,
        ) as ILightWeightArea[];
        const accessRightAvailableAreas: ILightWeightArea[] =
            availableAccessRightFilterCollection.AccessRightCoreFilter.Areas;
        const allAreas: ILightWeightArea[] = [
            ...fiItemsFlatten(accessRightAvailableAreas),
            ...fiItemsFlatten(outOfScopeAreas),
        ];
        return todoList.AccessRightFilterCollection.AccessRightCoreFilter.AreaIds.filter((selectedAreaId: number) => {
            return !!allAreas.find((area: ILightWeightArea) => {
                return selectedAreaId === area.Id && !area.ChildItems?.length;
            });
        })?.length;
    }

    protected getSharedUnitsAmountLabel(): string {
        const sharedBusinessUnitsAmount = this.getSharedUnitsAmount();

        return `${sharedBusinessUnitsAmount} ${this.appTextService
            .get(sharedBusinessUnitsAmount === 1 ? 'common.unit' : 'common.units')
            .toLowerCase()}`;
    }

    protected getSharedUnitsAmount(): number {
        const todoList = this.fiTodoListStoreService.currentTodoListData();
        return todoList.AccessRightFilterCollection.AccessRightCoreFilter.BusinessUnitIds.length;
    }
    protected getAreasAmountLabel(availableAccessRightFilterCollection: IAccessRightFilterCollection): string {
        const sharingNoMainAreasLength: number = this.getSelectedNoMainAreasLength(
            availableAccessRightFilterCollection,
        );
        return `${sharingNoMainAreasLength} ${this.appTextService
            .get(sharingNoMainAreasLength === 1 ? 'common.area' : 'common.areas')
            .toLowerCase()}`;
    }

    getSharingSettingsSectionDescription(availableAccessRightFilterCollection: IAccessRightFilterCollection): string {
        const todoList = this.fiTodoListStoreService.currentTodoListData();
        if (!todoList.IsShared) {
            return `<span class="todo-list-sharing-settings-badge not-shared">${this.appTextService.get('common.not_shared')}</span>`;
        }
        const sharingNoMainAreasLength: number = this.getSelectedNoMainAreasLength(
            availableAccessRightFilterCollection,
        );
        const sharedUnitsAmount = this.getSharedUnitsAmount();
        return `<span>
            ${this.appTextService.get('checklist.sharing_settings_message')}
            </span>
            <span class="${sharingNoMainAreasLength > 0 ? 'BrandedColor' : ''}">
                ${this.getAreasAmountLabel(availableAccessRightFilterCollection)}
            </span>
             ${this.appTextService.get('and').toLowerCase()} 
            <span class="${sharedUnitsAmount > 0 ? 'BrandedColor' : ''}">${this.getSharedUnitsAmountLabel()}</span>`;
    }

    confirmTodoItemRemove(todoItem: ITodoItem, sectionIndex: number, todoListItemIndex: number): void {
        const connectedOnboardings = this.fiTodoListStoreService.todoListConnectedOnboardings();
        this.fiDeleteConnectedOboardingElementDialogService
            .openDialog({
                ObjectType: ObjectTypes.TodoItem,
                ConnectedOnboardings: connectedOnboardings,
                HasResponses: todoItem.HasResponses,
                isNewElement: !isDefined(todoItem.Id),
            })
            .onYesButtonClick(() => {
                const sections = this.fiTodoListStoreService.currentTodoListSections();
                sections[sectionIndex].Items.splice(todoListItemIndex, 1);
                this.onSectionsDataChanged(sections);
            });
    }

    confirmTodoSectionRemove(sectionIndex: number): void {
        const sections = this.fiTodoListStoreService.currentTodoListSections();
        const currentSection = sections[sectionIndex];
        const connectedOnboardings = this.fiTodoListStoreService.todoListConnectedOnboardings();
        const sectionHasResponses = currentSection.Items.some((item) => item.HasResponses);
        this.fiDeleteConnectedOboardingElementDialogService
            .openDialog({
                ObjectType: ObjectTypes.TodoSection,
                ConnectedOnboardings: connectedOnboardings,
                HasResponses: sectionHasResponses,
                isNewElement: !sectionHasResponses,
            })
            .onYesButtonClick(() => {
                sections.splice(sectionIndex, 1);
                this.onSectionsDataChanged(sections);
            });
    }

    copyTodoItem(sectionIndex: number, todoListItemIndex: number): void {
        const sections = this.fiTodoListStoreService.currentTodoListSections();
        const todoListItemCopy = cloneDeep(sections[sectionIndex].Items[todoListItemIndex]);
        delete todoListItemCopy.Id;
        todoListItemCopy.HasResponses = false;
        todoListItemCopy.Names.forEach((item) => {
            if (item.Value) {
                const copyText = item.ContentLanguageId === FiApplicationLanguageType.Swedish ? 'Kopia' : 'Copy';
                item.Value = `(${copyText}) ` + item.Value;
            }
            delete item.Id;
        });
        sections[sectionIndex].Items.push(todoListItemCopy);
        this.onSectionsDataChanged(sections);
    }

    navigateToTodoListEdit(): void {
        const todoListId = this.fiTodoListStoreService.currentTodoListData()?.Id;
        if (todoListId) {
            this.fiStateService.go(FiStateNames.Onboarding.Todo.Edit._name, { id: todoListId });
        } else {
            this.fiStateService.go(FiStateNames.Onboarding.Todo.Add._name);
        }
    }

    navigateToSectionsEditor(): void {
        const todoListId = this.fiTodoListStoreService.currentTodoListData()?.Id;
        if (todoListId) {
            this.fiStateService.go(FiStateNames.Onboarding.Todo.Edit.Sections._name, { id: todoListId });
        } else {
            this.fiStateService.go(FiStateNames.Onboarding.Todo.Add.Sections._name);
        }
    }

    navigateToTodoItemEditor(): void {
        const todoListId = this.fiTodoListStoreService.currentTodoListData()?.Id;
        if (todoListId) {
            this.fiStateService.go(FiStateNames.Onboarding.Todo.Edit.Sections.Item, { id: todoListId });
        } else {
            this.fiStateService.go(FiStateNames.Onboarding.Todo.Add.Sections.Item);
        }
    }

    showAllItemsMandatoryErrorDialog(): void {
        this.fiDialogsService.notify({
            messageText: this.appTextService.get('common.error_all_mandatory'),
            headerText: this.appTextService.get('common.warning'),
            config: {
                panelClass: ['fi-rounded-dialog-panel'],
                autoFocus: false,
                restoreFocus: false,
            },
        });
    }

    openSharingSettingsDialog(): void {
        const todoListItem = this.fiTodoListStoreService.currentTodoListData();

        this.fiItemSharingSettingsDialogService
            .openItemSharingSettingsDialog(
                {
                    isShared: todoListItem.IsShared,
                    showSharingSelection: true,
                    businessUnitIdsOutOfScope: this.fiTodoListStoreService.businessUnitIdsOutOfScope(),
                    isItemOwner: todoListItem.CreatedByUserId === this.fiUserDataService.userData.UserId,
                    savedQuickSelectionFilters: this.fiTodoListStoreService.todoListQuickSelectionFilters(),
                    selectedFiltersCollection: this.fiTodoListStoreService.selectedFiltersCollection(),
                    availableAccessRightFilterCollection:
                        this.fiTodoListStoreService.todoListAccessRightFilterCollection(),
                    itemAccessRightCoreFilter: cloneDeep(
                        todoListItem.AccessRightFilterCollection.AccessRightCoreFilter,
                    ),
                    itemAccessRightUserFilter: cloneDeep(
                        todoListItem.AccessRightFilterCollection.AccessRightUserFilter,
                    ),
                    dialogDescriptionText: this.appTextService.get('onboarding.todo_list_sharing_settings_description'),
                    individualUnitSectionDescriptionText: this.appTextService.get(
                        'onboarding.todo_list_sharing_settings_units_description',
                    ),
                    moduleType: ModuleType.Onboarding,
                    showRolesCheckboxes: true,
                    sharingSelectionOptions: [
                        {
                            value: false,
                            label: this.appTextService.get('task.private'),
                        },
                        {
                            value: true,
                            label: this.appTextService.get('common.public'),
                        },
                    ],
                },
                this.appTextService.get('sharing_settings'),
            )
            .onYesButtonClick((result) => {
                this.fiTodoListStoreService.updateTodoListData((current) => ({
                    ...current,
                    AccessRightFilterCollection: {
                        ...current.AccessRightFilterCollection,
                        AccessRightCoreFilter: result.accessRightCoreFilter,
                    },
                    IsShared: result.isShared,
                }));

                this.fiTodoListStoreService.setSelectedFiltersCollection(result.selectedFiltersCollection);
                this.fiTodoListStoreService.setTodoListQuickSelectionFilters(
                    cloneDeep(result.savedQuickSelectionFilters),
                );
            });
    }

    setInitialSelectedLanguageId(todoList: ITodoListEditItem): void {
        const selectedLanguageId = this.fiContentLanguageService.getContentLanguageIdByFilledModels(todoList.Names);
        this.fiTodoListStoreService.setSelectedLanguageId(selectedLanguageId);
    }

    setInitialSavedQuickSelectionFilters(): void {
        const todoListQuickSelectionFilters = this.fiTodoListStoreService.todoListQuickSelectionFilters();
        const currentTodoListData = this.fiTodoListStoreService.currentTodoListData();
        const accessRightFilterCollection = this.fiTodoListStoreService.todoListAccessRightFilterCollection();

        const mergeResult = mergeLightWeightObjects(
            accessRightFilterCollection.AccessRightCoreFilter.BusinessUnits,
            currentTodoListData.AccessRightFilterCollection.AccessRightCoreFilter.BusinessUnits,
        );

        this.fiTodoListStoreService.setBusinessUnitIdsOutOfScope(mergeResult.disabledItemIds);
        this.fiTodoListStoreService.setTodoListAccessRightFilterCollection({
            ...accessRightFilterCollection,
            AccessRightCoreFilter: {
                ...accessRightFilterCollection.AccessRightCoreFilter,
                BusinessUnits: mergeResult.mergedResult,
            },
        });

        const selectedFiltersCollection = this.fiItemSharingSettingsDialogService.getSavedFilterBySharingSelection(
            currentTodoListData.AccessRightFilterCollection.AccessRightCoreFilter,
            todoListQuickSelectionFilters,
            true,
        );
        this.fiTodoListStoreService.setSelectedFiltersCollection(selectedFiltersCollection);
    }

    getSectionsWithFilledPlaceholderNames(sections: ITodoSection[]): ITodoSection[] {
        return sections.map((section: ITodoSection) => {
            return this.fiSectionsHelperService.setDefaultSectionName(
                section,
                this.fiTodoListStoreService.selectedLanguageId(),
                ObjectTypes.TodoSection,
            );
        });
    }

    readonly TodoListEditorSections: Readonly<ISectionCardData[]> = [
        {
            headerTextLabel: this.appTextService.get('todo.todo_items'),
            subheaderTextLabel: this.appTextService.get('todo.todo_items_section_description'),
            id: FiTodoListEditorSections.TodoItems,
        },
        {
            headerTextLabel: this.appTextService.get('sharing_settings'),
            subheaderTextLabel: this.appTextService.get('todo.sharing_settings_section_description'),
            id: FiTodoListEditorSections.SharingSettings,
        },
    ];
}
